import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@order-analyzer/ui";
import { LockIcon } from "lucide-react";
import { api } from "../api.js";
import { useAuth } from "../auth.js";
import { Markdown } from "../components/Markdown.js";

interface BlockedNoticeResponse {
  blocked: boolean;
  blockedAt: string | null;
  noticeMarkdown: string | null;
}

/**
 * Vollbildseite, die statt der App gerendert wird, wenn der angemeldete Benutzer gesperrt ist.
 * Laedt den in der Administration konfigurierten Markdown-Text ueber den Whitelist-Endpunkt
 * /api/users/me/blocked-notice (siehe auth.ts BLOCK_GUARD_ALLOWLIST). Alle anderen App-Requests
 * antworten mit 403 USER_BLOCKED - die App-Routen sind fuer den Benutzer effektiv gesperrt.
 */
export function BlockedUserScreen() {
  const auth = useAuth();
  const query = useQuery({
    queryKey: ["blocked-user-notice"],
    queryFn: () => api.get<BlockedNoticeResponse>("/users/me/blocked-notice"),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950">
      <Card className="max-w-xl">
        <CardContent className="flex flex-col gap-4 p-6">
          <div className="flex items-center gap-3">
            <LockIcon className="h-6 w-6 text-amber-500" aria-hidden="true" />
            <h1 className="text-xl font-semibold">Zugriff nicht moeglich</h1>
          </div>
          {query.isLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Hinweis wird geladen ...</p>
          )}
          {query.data?.noticeMarkdown && (
            <Markdown source={query.data.noticeMarkdown} className="text-sm text-slate-700 dark:text-slate-200" />
          )}
          {query.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              Der hinterlegte Hinweistext konnte nicht geladen werden. Bitte wenden Sie sich an Ihre Administration.
            </p>
          )}
          <div className="mt-4 rounded-md bg-slate-100 p-3 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
            Angemeldet als {auth.user?.displayName ?? auth.user?.email}. Um sich mit einem anderen
            Konto anzumelden, melden Sie sich bitte vom Microsoft-Konto in Ihrem Browser ab.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
